import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  ntGetNaspTracker,
  ntPollNaspTracker,
  ntGetNaspTrackerById,
  ntGetNaspTrackerDropdowns,
  ntUpdateNaspTracker,
  ntAddNaspTrackerComment,
  ntExportNaspTracker,
  ntGetNaspTrackerClosed,
  ntPollNaspTrackerClosed,
} from '../actions/naspTrackerThunks';
import './NaspTracker.css';

const useStyles = makeStyles((theme) => ({
  root:      { padding: theme.spacing(3) },
  container: { marginTop: theme.spacing(3) },
}));

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const TYPE_TABS = ['Availability', 'Build', 'Spare', 'Outage'];

const pad2 = (n) => String(n).padStart(2, '0');

// Backend sometimes sends dates as ambiguous "D/M/YYYY" slash strings instead
// of ISO. new Date('12/08/2026') assumes US MM/DD/YYYY, silently swapping day
// and month. Parse slash-separated dates explicitly as DD/MM/YYYY instead.
const parseFlexibleDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (match) {
      const [, d, m, y, hh = '0', mm = '0', ss = '0'] = match;
      return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss));
    }
  }
  return new Date(value);
};

const formatDate = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = parseFlexibleDate(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
};

const formatDateTime = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = parseFlexibleDate(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

const formatDateTimeShort = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = parseFlexibleDate(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

const toDateInput = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = parseFlexibleDate(value);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  } catch { return ''; }
};

const toDateTimeInput = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = parseFlexibleDate(value);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  } catch { return ''; }
};

const searchMatch = (row, q) => {
  if (!q) return true;
  const s = q.toLowerCase();
  return [
    row.id, row.insertUser, row.jvIds, row.techs, row.case, row.daysOos, row.coosCount,
    row.source, row.type, row.owner, row.status,
  ].some((v) => v != null && String(v).toLowerCase().includes(s));
};

// ─── ICON: EDIT ───────────────────────────────────────────────────────────────
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

// ─── ICON: DOWNLOAD ───────────────────────────────────────────────────────────
const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

// ─── STATUS DOT ───────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  'open':        '#16a34a',
  'in progress': '#eab308',
  'closed':      '#2563eb',
};

const StatusDot = ({ status }) => {
  const color = STATUS_COLORS[String(status ?? '').trim().toLowerCase()] ?? '#94a3b8';
  return <span className="nt-status-dot" style={{ backgroundColor: color }} title={status} />;
};

// ─── TYPE BADGE ───────────────────────────────────────────────────────────────
const TYPE_COLORS = {
  'availability': { color: '#2563eb', background: '#eff6ff' },
  'build':        { color: '#16a34a', background: '#f0fdf4' },
  'spare':        { color: '#d97706', background: '#fffbeb' },
  'outage':       { color: '#dc2626', background: '#fef2f2' },
};

const TypeBadge = ({ type }) => {
  if (!type) return null;
  const { color, background } = TYPE_COLORS[String(type).trim().toLowerCase()] ?? { color: '#475569', background: '#f1f5f9' };
  return <span className="nt-type-badge" style={{ color, backgroundColor: background }}>{type}</span>;
};

// ─── ICON: EYE ────────────────────────────────────────────────────────────────
const IconEye = ({ color = 'currentColor' }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

// ─── CLOSED TOGGLE ────────────────────────────────────────────────────────────
const ClosedToggle = ({ value, onChange }) => (
  <button
    className={`nt-btn nt-btn-ghost${value ? ' nt-btn-closed--active' : ''}`}
    onClick={() => onChange(!value)}
    title={value ? 'Showing closed — click to hide' : 'Show closed'}
  >
    <IconEye color={value ? '#7c3aed' : '#94a3b8'} />
    Closed
  </button>
);

// ─── DATE FIELD (dd/mm/yyyy display, native picker underneath) ─────────────
// For plain dates. For date+time, the native time widget follows the OS/
// browser locale (12h/AM-PM on many setups) and can't be forced to 24h —
// so DateTimeField below pairs the native date picker with our own 24h
// hour/minute selects instead.
const DateField = ({ value, onChange, readOnly }) => {
  const nativeRef = useRef(null);

  const openPicker = () => {
    if (readOnly) return;
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') el.showPicker();
    else el.focus();
  };

  return (
    <div className="nt-date-wrapper">
      <input
        className={`nt-edit-input nt-date-display${readOnly ? ' nt-edit-input--readonly' : ''}`}
        type="text"
        value={value ? formatDate(value) : ''}
        placeholder="dd/mm/yyyy"
        readOnly
        onClick={openPicker}
      />
      <input
        ref={nativeRef}
        className="nt-date-native"
        type="date"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        tabIndex={-1}
      />
    </div>
  );
};

// ─── DATETIME FIELD (dd/mm/yyyy + 24h hour/minute selects) ─────────────────
const HOURS   = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad2(i));

const DateTimeField = ({ value, onChange, readOnly }) => {
  const nativeRef = useRef(null);

  const [datePart, timePart] = (value || '').split('T');
  const [hour, minute] = (timePart || '').split(':');

  const openPicker = () => {
    if (readOnly) return;
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') el.showPicker();
    else el.focus();
  };

  const combine = (nextDate, nextHour, nextMinute) => {
    if (!nextDate) { onChange(''); return; }
    onChange(`${nextDate}T${nextHour || '00'}:${nextMinute || '00'}`);
  };

  return (
    <div className="nt-datetime-wrapper">
      <div className="nt-date-wrapper nt-datetime-date">
        <input
          className={`nt-edit-input nt-date-display${readOnly ? ' nt-edit-input--readonly' : ''}`}
          type="text"
          value={datePart ? formatDate(datePart) : ''}
          placeholder="dd/mm/yyyy"
          readOnly
          onClick={openPicker}
        />
        <input
          ref={nativeRef}
          className="nt-date-native"
          type="date"
          value={datePart ?? ''}
          onChange={(e) => combine(e.target.value, hour, minute)}
          disabled={readOnly}
          tabIndex={-1}
        />
      </div>
      <select
        className="nt-edit-input nt-time-select"
        value={hour ?? ''}
        onChange={(e) => combine(datePart, e.target.value, minute)}
        disabled={readOnly || !datePart}
      >
        <option value="" disabled>HH</option>
        {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="nt-time-sep">:</span>
      <select
        className="nt-edit-input nt-time-select"
        value={minute ?? ''}
        onChange={(e) => combine(datePart, hour, e.target.value)}
        disabled={readOnly || !datePart}
      >
        <option value="" disabled>mm</option>
        {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  );
};

// ─── EDITABLE FIELDS ──────────────────────────────────────────────────────────
const EDITABLE_FIELDS = [
  { key: 'source',    label: 'Source',    type: 'text',   readOnly: true },
  { key: 'type',      label: 'Type',      type: 'text',   readOnly: true },
  { key: 'buildType',     label: 'Build Type',     type: 'select' },
  { key: 'owner',         label: 'Owner',          type: 'select' },
  { key: 'status',        label: 'Status',         type: 'select' },
  { key: 'nextFollowUp',  label: 'Next Follow Up', type: 'datetime' },
];

// ─── COMPLETE COLUMNS (NaspTrackerComplete) ──────────────────────────────────
const COMPLETE_COLUMNS = [
  { key: 'jvId',      label: 'JV Id'     },
  { key: 'tech',      label: 'Tech'      },
  { key: 'csr',       label: 'CSR'       },
  { key: 'vendor',    label: 'Vendor'    },
  { key: 'avail',     label: 'Avail'     },
  { key: 'queue',     label: 'Queue'     },
  { key: 'dateOos',   label: 'Date OOS'  },
  { key: 'daysOos',   label: 'Days OOS'  },
  { key: 'polygon',   label: 'Polygon'   },
  { key: 'eta',       label: 'ETA', width: 78 },
  { key: 'locked',    label: 'Locked'    },
  { key: 'more',      label: 'More'      },
  { key: 'coosCount', label: 'COOs'      },
];

// ─── MESSAGE MODAL ────────────────────────────────────────────────────────────
function MessageModal({ message, title = 'More', onClose }) {
  return createPortal(
    <div className="nt-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nt-modal" style={{ maxWidth: 480 }}>
        <div className="nt-modal-header">
          <span className="nt-modal-title">{title}</span>
          <button className="nt-close-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="nt-modal-body">
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: "'Inter', sans-serif" }}>
            {message}
          </p>
        </div>
        <div className="nt-modal-footer">
          <button className="nt-btn nt-btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── EDIT MODAL ───────────────────────────────────────────────────────────────
function EditModal({ row, complete, comments, dropdowns, loadingDropdowns, loading, loadingComment, onClose, onSave, onAddComment, userId }) {
  const [form,        setForm]        = useState(() => {
    const init = { ...row };
    EDITABLE_FIELDS.forEach((f) => {
      if (f.type === 'date') init[f.key] = toDateInput(row[f.key]);
      if (f.type === 'datetime') init[f.key] = toDateTimeInput(row[f.key]);
    });
    return init;
  });
  const [newComment,     setNewComment]     = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const commentsEndRef              = useRef(null);

  // scroll comments to bottom on open/update
  useEffect(() => {
    if (commentsEndRef.current)
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length]);

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    onAddComment({ tableId: row.id, comment: newComment.trim() }, () => setNewComment(''));
  };

  const fieldByKey = (key) => EDITABLE_FIELDS.find((f) => f.key === key);
  const showBuildType = form.type === 'Build';
  const fieldSlots = showBuildType
    ? [
        { type: 'group', keys: ['source', 'type'] },
        { type: 'single', key: 'buildType' },
        { type: 'single', key: 'owner' },
        { type: 'single', key: 'status' },
        { type: 'single', key: 'nextFollowUp' },
      ]
    : [
        { type: 'single', key: 'source' },
        { type: 'single', key: 'type' },
        { type: 'single', key: 'owner' },
        { type: 'single', key: 'status' },
        { type: 'single', key: 'nextFollowUp' },
      ];

  const renderField = (f) => {
    if (f.type === 'select') {
      const options = dropdowns?.[f.key] ?? [];
      return (
        <select
          className="nt-edit-input"
          value={form[f.key] ?? ''}
          onChange={(e) => handleChange(f.key, e.target.value)}
          disabled={loadingDropdowns}
        >
          <option value="">{loadingDropdowns ? 'Loading…' : '—'}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.text}</option>
          ))}
        </select>
      );
    }
    if (f.type === 'datetime') {
      return (
        <DateTimeField
          value={form[f.key] ?? ''}
          onChange={(v) => handleChange(f.key, v)}
          readOnly={f.readOnly}
        />
      );
    }
    if (f.type === 'date') {
      return (
        <DateField
          value={form[f.key] ?? ''}
          onChange={(v) => handleChange(f.key, v)}
          readOnly={f.readOnly}
        />
      );
    }
    return (
      <input
        className={`nt-edit-input${f.readOnly ? ' nt-edit-input--readonly' : ''}`}
        type={f.type}
        value={form[f.key] ?? ''}
        onChange={(e) => handleChange(f.key, e.target.value)}
        readOnly={f.readOnly}
      />
    );
  };

  return createPortal(
    <div className="nt-modal-overlay">
      {selectedMessage && (
        <MessageModal message={selectedMessage} onClose={() => setSelectedMessage(null)} />
      )}
      <div className="nt-modal">
        <div className="nt-modal-header">
          <span className="nt-modal-title">Edit - Case: {row.case}</span>
          <button className="nt-close-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="nt-modal-body">
          {/* ── Complete table ── */}
          <div className="nt-complete-section">
            <span className="nt-comments-title">Details</span>
            <div className="nt-complete-table-wrapper">
              <table className="nt-complete-table">
                <thead>
                  <tr>
                    {COMPLETE_COLUMNS.map((col) => (
                      <th key={col.key} style={col.width ? { width: col.width, maxWidth: col.width } : undefined}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!complete || complete.length === 0 ? (
                    <tr>
                      <td colSpan={COMPLETE_COLUMNS.length} className="nt-table-empty">No data available</td>
                    </tr>
                  ) : complete.map((c, idx) => (
                    <tr key={c.id ?? idx}>
                      {COMPLETE_COLUMNS.map((col) => {
                        if (col.key === 'more' || col.key === 'queue') {
                          const value = c[col.key];
                          return (
                            <td
                              key={col.key}
                              className={`nt-table-msg${value ? ' nt-table-msg-clickable' : ''}`}
                              onClick={() => value && setSelectedMessage(value)}
                              title={value}
                              style={col.key === 'queue' ? { maxWidth: '20ch' } : undefined}
                            >
                              {value}
                            </td>
                          );
                        }
                        return (
                          <td key={col.key}>
                            {col.key === 'dateOos' || col.key === 'eta' ? formatDate(c[col.key]) : c[col.key]}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Fields grid ── */}
          <div className="nt-edit-grid">
            {fieldSlots.map((slot) => (
              slot.type === 'group' ? (
                <div key={slot.keys.join('-')} className="nt-edit-field-group">
                  {slot.keys.map((key) => {
                    const f = fieldByKey(key);
                    return (
                      <div key={key} className="nt-edit-field nt-edit-field-half">
                        <label className="nt-edit-label">{f.label}</label>
                        {renderField(f)}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div key={slot.key} className="nt-edit-field">
                  <label className="nt-edit-label">{fieldByKey(slot.key).label}</label>
                  {renderField(fieldByKey(slot.key))}
                </div>
              )
            ))}
          </div>

          {/* ── Comments ── */}
          <div className="nt-comments-section">
            <span className="nt-comments-title">Comments</span>

            {/* List */}
            <div className="nt-comments-list">
              {comments.length === 0 ? (
                <p className="nt-comments-empty">No comments yet.</p>
              ) : comments.map((c) => (
                <div key={c.id} className="nt-comment-item">
                  <div className="nt-comment-meta">
                    <span className="nt-comment-date">{formatDateTime(c.insertDate)}</span>
                    <span className="nt-comment-user">{c.insertUser}</span>
                  </div>
                  <span className="nt-comment-text">{c.comment}</span>
                </div>
              ))}
              <div ref={commentsEndRef} />
            </div>

            {/* New comment input */}
            <div className="nt-comment-new">
              <input
                className="nt-edit-input"
                type="text"
                placeholder="Add a comment…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                disabled={loadingComment}
              />
              <button
                className="nt-btn nt-btn-primary"
                onClick={handleAddComment}
                disabled={loadingComment || !newComment.trim()}
                style={{ flexShrink: 0 }}
              >
                {loadingComment ? <span className="nt-spinner" style={{ width: 12, height: 12 }} /> : null}
                Add
              </button>
            </div>
          </div>
        </div>

        <div className="nt-modal-footer">
          <button className="nt-btn nt-btn-ghost" onClick={onClose} disabled={loading}>Close</button>
          <button className="nt-btn nt-btn-primary" onClick={() => onSave(form)} disabled={loading}>
            {loading ? <span className="nt-spinner" style={{ width: 12, height: 12 }} /> : null}
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── SORT ICON ────────────────────────────────────────────────────────────────
const SortIcon = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}>
      <polyline points="6 9 12 3 18 9"/><polyline points="6 15 12 21 18 15"/>
    </svg>
  );
  return sortDir === 'asc'
    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}><polyline points="6 9 12 3 18 9"/></svg>
    : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, flexShrink: 0 }}><polyline points="6 15 12 21 18 15"/></svg>;
};

// ─── TABLE ────────────────────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'jvIds',      label: 'JV IDs'      },
  { key: 'techs',      label: 'TECHs'       },
  { key: 'case',       label: 'Case'        },
  { key: 'daysOos',    label: 'Days OOS'    },
  { key: 'eta',        label: 'ETA', width: 78 },
  { key: 'coosCount',  label: 'COOs'        },
  { key: 'source',     label: 'Source'      },
  { key: 'type',       label: 'Type'        },
  { key: 'owner',      label: 'Owner'       },
  { key: 'status',     label: 'Status'      },
  { key: 'insertDate',    label: 'Insert Date'   },
  { key: 'updated',       label: 'Updated'       },
  { key: 'nextFollowUp',  label: 'Next Follow Up'  },
];

function NaspTrackerTable({ rows, loading, loadingUpdate, loadingComment, naspTrackerById, loadingById, dropdowns, loadingDropdowns, onGearClick, onSave, onAddComment, onCloseModal, userId, userFlag }) {
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search,   setSearch]   = useState('');
  const [sortCol,  setSortCol]  = useState(null);
  const [sortDir,  setSortDir]  = useState('asc');
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => { setPage(1); }, [rows.length, search, pageSize]);

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  const handleSort = (key) => {
    if (sortCol === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const filtered = rows.filter((r) => searchMatch(r, search));

  const sorted = sortCol
    ? [...filtered].sort((a, b) => {
        const va = a[sortCol] ?? '';
        const vb = b[sortCol] ?? '';
        const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged      = sorted.slice((page - 1) * pageSize, page * pageSize);

  const isBuildTab = userFlag === 'Build';
  const columns = isBuildTab
    ? COLUMNS.flatMap((col) => (
        col.key === 'type'
          ? [col, { key: 'buildType', label: 'Build Type' }]
          : [col]
      ))
    : COLUMNS;
  const colSpan = columns.length + 1;

  const modalRow      = naspTrackerById?.naspTracker         ?? null;
  const modalComplete = naspTrackerById?.naspTrackerComplete ?? [];
  const modalComments = naspTrackerById?.naspTrackerComments ?? [];

  const handleSave = (form) => onSave(form, onCloseModal);

  return (
    <div>
      {selectedMessage && (
        <MessageModal message={selectedMessage} title="JV IDs" onClose={() => setSelectedMessage(null)} />
      )}

      {modalRow && (
        <EditModal
          row={modalRow}
          complete={modalComplete}
          comments={modalComments}
          dropdowns={dropdowns}
          loadingDropdowns={loadingDropdowns}
          loading={loadingUpdate}
          loadingComment={loadingComment}
          onClose={onCloseModal}
          onSave={handleSave}
          onAddComment={onAddComment}
          userId={userId}
        />
      )}

      <div className="nt-search-bar">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          className="nt-search-input"
          type="text"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="nt-search-clear" onClick={() => setSearch('')}>✕</button>
        )}
      </div>

      <div className="nt-table-wrapper">
        <table className="nt-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="nt-th-sortable"
                  onClick={() => handleSort(col.key)}
                  style={col.width ? { width: col.width, maxWidth: col.width } : undefined}
                >
                  {col.label}
                  <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                </th>
              ))}
              <th style={{ textAlign: 'center', width: 48 }}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="nt-table-empty">
                  <span className="nt-spinner" style={{ marginRight: 8 }} />
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="nt-table-empty">No data available</td>
              </tr>
            ) : paged.map((row) => (
              <tr key={row.id}>
                <td
                  className={`nt-table-msg${row.jvIds ? ' nt-table-msg-clickable' : ''}`}
                  onClick={() => row.jvIds && setSelectedMessage(row.jvIds)}
                  title={row.jvIds}
                  style={{ maxWidth: '20ch' }}
                >
                  {row.jvIds}
                </td>
                <td>{row.techs}</td>
                <td>{row.case}</td>
                <td>{row.daysOos}</td>
                <td>{formatDate(row.eta)}</td>
                <td>{row.coosCount}</td>
                <td>{row.source}</td>
                <td><TypeBadge type={row.type} /></td>
                {isBuildTab && <td>{row.buildType}</td>}
                <td>{row.owner}</td>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <StatusDot status={row.status} />
                    {row.status}
                  </span>
                </td>
                <td>{formatDate(row.insertDate)}</td>
                <td>{formatDateTimeShort(row.updated)}</td>
                <td>{formatDateTimeShort(row.nextFollowUp)}</td>
                <td style={{ textAlign: 'center' }}>
                  <button className="nt-gear-btn" onClick={() => onGearClick(row.id)} title="Edit">
                    <IconEdit />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && (
        <div className="nt-pagination">
          <div className="nt-page-size">
            <span>Rows per page:</span>
            <select
              className="nt-page-size-select"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {filtered.length > pageSize && (
            <div className="nt-page-nav">
              <button className="nt-page-btn" onClick={() => setPage(1)}            disabled={page === 1}>«</button>
              <button className="nt-page-btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
              <span className="nt-page-info">{page} / {totalPages} <span className="nt-page-total">({filtered.length})</span></span>
              <button className="nt-page-btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
              <button className="nt-page-btn" onClick={() => setPage(totalPages)}   disabled={page === totalPages}>»</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function NaspTracker() {
  const classes             = useStyles();
  const dispatch            = useDispatch();
  const loading             = useSelector((state) => state.naspTrackerReducer.loading);
  const naspTracker         = useSelector((state) => state.naspTrackerReducer.naspTracker);
  const naspTrackerClosed   = useSelector((state) => state.naspTrackerReducer.naspTrackerClosed);
  const naspTrackerById     = useSelector((state) => state.naspTrackerReducer.naspTrackerById);
  const naspTrackerDropdowns = useSelector((state) => state.naspTrackerReducer.naspTrackerDropdowns);
  const userId              = useSelector((state) => state.session.user?.id);

  const [userFlag, setUserFlag] = useState(() => userId ?? 'All');
  const [closed,   setClosed]   = useState(false);
  const userFlagRef             = useRef(userId ?? 'All');
  const closedRef               = useRef(false);
  const pollingRef              = useRef(null);

  useEffect(() => { closedRef.current = closed; }, [closed]);

  const stopPolling = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  };

  const startPolling = () => {
    pollingRef.current = setInterval(() => {
      if (closedRef.current) {
        dispatch(ntPollNaspTrackerClosed(userFlagRef.current));
      } else {
        dispatch(ntPollNaspTracker(userFlagRef.current));
      }
    }, 5000);
  };

  useEffect(() => {
    dispatch(ntGetNaspTracker(userFlag));
    dispatch(ntGetNaspTrackerDropdowns());
    stopPolling();
    startPolling();
    return () => stopPolling();
  }, []);

  const handleToggle = (flag) => {
    userFlagRef.current = flag;
    setUserFlag(flag);
    stopPolling();
    if (closedRef.current) {
      dispatch(ntGetNaspTrackerClosed(flag));
    } else {
      dispatch(ntGetNaspTracker(flag));
    }
    startPolling();
  };

  const handleClosedToggle = (value) => {
    closedRef.current = value;
    setClosed(value);
    stopPolling();
    if (value) {
      dispatch(ntGetNaspTrackerClosed(userFlagRef.current));
    } else {
      dispatch(ntGetNaspTracker(userFlagRef.current));
    }
    startPolling();
  };

  const handleGearClick = (id) => {
    dispatch(ntGetNaspTrackerById(id));
  };

  const handleCloseModal = () => {
    dispatch({ type: 'NT_GET_NASP_TRACKER_BY_ID_SUCCESS', payload: null });
  };

  const handleSave = (form, onClose) => {
    dispatch(ntUpdateNaspTracker(userId, form, () => {
      dispatch(ntGetNaspTracker(userFlagRef.current));
      if (onClose) onClose();
    }));
  };

  const handleAddComment = (dto, onSuccess) => {
    dispatch(ntAddNaspTrackerComment(userId, { ...dto, insertUser: userId }, onSuccess));
  };

  const handleExport = () => {
    dispatch(ntExportNaspTracker(userId));
  };

  return (
    <Page className={classes.root} title="Availability Tracker">
      <Header module="NASP" view="Availability Tracker" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="nt-root">
            <div style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── NASP Tracker ───────────────────────────────────────────── */}
              <div className="nt-card">
                <div className="nt-card-header">
                  <div className="nt-tabs">
                    <button
                      className={`nt-tab${userFlag === userId ? ' nt-tab--active' : ''}`}
                      onClick={() => handleToggle(userId)}
                    >
                      Assigned to me
                    </button>
                    {TYPE_TABS.map((t) => (
                      <button
                        key={t}
                        className={`nt-tab${userFlag === t ? ' nt-tab--active' : ''}`}
                        onClick={() => handleToggle(t)}
                      >
                        {t}
                      </button>
                    ))}
                    <button
                      className={`nt-tab${userFlag === 'All' ? ' nt-tab--active' : ''}`}
                      onClick={() => handleToggle('All')}
                    >
                      All
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <ClosedToggle value={closed} onChange={handleClosedToggle} />
                    <button
                      className="nt-btn nt-btn-export"
                      onClick={handleExport}
                      disabled={loading.exportNaspTracker}
                      title="Export"
                    >
                      {loading.exportNaspTracker
                        ? <span className="nt-spinner" style={{ width: 12, height: 12 }} />
                        : <IconDownload />}
                      Export
                    </button>
                  </div>
                </div>
                <div style={{ padding: 0 }}>
                  <NaspTrackerTable
                    rows={closed ? naspTrackerClosed : naspTracker}
                    loading={closed ? loading.naspTrackerClosed : loading.naspTracker}
                    userFlag={userFlag}
                    loadingById={loading.naspTrackerById}
                    loadingUpdate={loading.updateNaspTracker}
                    loadingComment={loading.addNaspTrackerComment}
                    naspTrackerById={naspTrackerById}
                    dropdowns={naspTrackerDropdowns}
                    loadingDropdowns={loading.naspTrackerDropdowns}
                    onGearClick={handleGearClick}
                    onSave={handleSave}
                    onAddComment={handleAddComment}
                    onCloseModal={handleCloseModal}
                    userId={userId}
                  />
                </div>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default NaspTracker;